import React, {
  CSSProperties,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { FiPlus as Plus } from 'react-icons/fi';
import { makeStyles } from 'tss-react/mui';

import { HTMLInputEvent } from 'src/constants/dataTypes';
import Button from 'src/legacy/components/Button';

const useStyles = makeStyles()(() => ({
  input: {
    display: 'none',
  },
  uploadButtonWrapper: {
    display: 'flex',
  },
  uploadButton: {
    '&.MuiButton-root.MuiButton-containedPrimary': {
      height: '40px',
    },
  },
  hidden: {
    position: 'absolute',
    top: -1000,
    zIndex: -100,
  },
}));

interface UploadButtonProps {
  handleChange: (e: HTMLInputEvent | any) => void;
  isUploading?: boolean;
  htmlId: string;
  label: string;
  styles?: CSSProperties;
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
  variant?: 'text' | 'outlined' | 'contained' | undefined;
  hasStartIcon?: boolean;
  hidden?: boolean;
  startIcon?: React.ReactElement;
  onEmptyFolderSelected?: () => void;
}

export type UploadButtonHandle = {
  clickUploadButton: (directoryUpload?: boolean) => void;
  inputFileRef?: HTMLInputElement & { directory: boolean };
};

export const UploadButton = forwardRef<UploadButtonHandle, UploadButtonProps>(
  (props, ref) => {
    const { classes } = useStyles();
    const {
      handleChange,
      isUploading,
      htmlId,
      label,
      styles,
      color = 'primary',
      hasStartIcon = true,
      variant = 'contained',
      hidden = false,
      startIcon,
      onEmptyFolderSelected,
    } = props;

    const inputFile = useRef<HTMLInputElement & { directory: boolean }>(null);

    const triggerInputFileClick = () => {
      if (inputFile && inputFile.current) {
        inputFile.current.click();
      }
    };

    const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(event);
      event.target.value = '';
    };

    // The browser's 'cancel' event fires when:
    // 1. User presses Escape or clicks Cancel in the file picker dialog
    // 2. User selects an empty folder and clicks Open (browser treats no files selected as cancel)
    // In both cases, we show an error if this was a folder upload attempt.
    const handleBrowserCancelEvent = React.useCallback(() => {
      if (inputFile.current?.webkitdirectory && onEmptyFolderSelected) {
        onEmptyFolderSelected();
      }
    }, [onEmptyFolderSelected]);

    React.useEffect(() => {
      const input = inputFile.current;
      if (!input) return;

      input.addEventListener('cancel', handleBrowserCancelEvent);

      return () => {
        input.removeEventListener('cancel', handleBrowserCancelEvent);
      };
    }, [handleBrowserCancelEvent]);

    useImperativeHandle(ref, () => ({
      clickUploadButton(uploadOnlyDirectory = false) {
        if (inputFile.current) {
          inputFile.current.webkitdirectory = uploadOnlyDirectory;
          inputFile.current.directory = uploadOnlyDirectory;
        }
        triggerInputFileClick();
      },
      inputFileRef: inputFile.current || undefined,
    }));

    const uploadButtonClick = () => {
      triggerInputFileClick();
    };

    return (
      <div className={`w-fit ${hidden ? classes.hidden : ''}`}>
        <input
          className={classes.input}
          id="icon-button-file"
          type="file"
          multiple
          ref={inputFile}
          onChange={handleChangeFile}
        />
        <Button
          htmlId={htmlId}
          variant={variant}
          aria-label="upload picture"
          component="span"
          color={color}
          isLoading={isUploading}
          startIcon={startIcon || (hasStartIcon && <Plus />)}
          style={styles}
          disableElevation
          progressColor="inherit"
          onClick={uploadButtonClick}
        >
          {label}
        </Button>
      </div>
    );
  },
);
